/**
 * Add-on Drawer JavaScript
 * Handles add-on product selection and cart integration
 */

class AddonDrawer {
  constructor() {
    this.drawer = document.getElementById('addon-drawer');
    this.openButtons = document.querySelectorAll('.open-addon-drawer');
    this.closeButtons = document.querySelectorAll('[data-addon-close]');
    this.selectButton = document.getElementById('addon-select-upgrade');
    
    this.selectedAddons = new Map();
    this.isAddingToCart = false; // Flag to prevent multiple cart operations
    this.formObserver = null; // Mutation observer for form changes
    
    // Global flag to prevent duplicate form submissions
    if (!window.addonDrawerProcessing) {
      window.addonDrawerProcessing = false;
    }
    
    this.init();
  }
  
  init() {
    if (!this.drawer) {
      return;
    }
    
    this.bindEvents();
    this.loadSavedAddons();
    this.interceptAddToCart(); // Intercept add to cart immediately
    
    // Set up a mutation observer to re-intercept when form changes
    this.setupFormObserver();
  }
  
  bindEvents() {
    // Open drawer - handle all buttons with class open-addon-drawer
    this.openButtons.forEach(button => {
      button.addEventListener('click', () => this.openDrawer());
    });
    
    // Close drawer
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.closeDrawer());
    });
    
    // Close on overlay click
    this.drawer?.addEventListener('click', (e) => {
      if (e.target === this.drawer || e.target.classList.contains('addon-drawer__overlay')) {
        this.closeDrawer();
      }
    });
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.closeDrawer();
      }
    });
    
    // Add/Remove addon items
    this.drawer?.addEventListener('click', (e) => {
      if (e.target.matches('[data-addon-toggle-button]') || e.target.closest('[data-addon-toggle-button]')) {
        const button = e.target.matches('[data-addon-toggle-button]') ? e.target : e.target.closest('[data-addon-toggle-button]');
        this.toggleAddon(button);
      }
    });
    
    // Variant selection
    this.drawer?.addEventListener('change', (e) => {
      if (e.target.matches('[data-addon-variant-select]')) {
        this.updateVariantPrice(e.target);
      }
    });
    
    // Select upgrades button
    this.selectButton?.addEventListener('click', () => this.selectUpgrades());
  }
  
  openDrawer() {
    if (!this.drawer) {
      return;
    }
    
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Reload addons from storage first
    this.loadSavedAddons();
    
    // Update display
    this.updateUIFromStorage();
  }
  
  closeDrawer() {
    if (!this.drawer) return;
    
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
  
  isOpen() {
    return this.drawer?.classList.contains('is-open');
  }
  
  toggleAddon(button) {
    const productCard = button.closest('.addon-product-card');
    const productId = productCard.dataset.addonProductId;
    const variantSelect = productCard.querySelector('[data-addon-variant-select]');
    const variantId = variantSelect.value;
    const price = parseInt(variantSelect.dataset.price);
    
    if (this.selectedAddons.has(variantId)) {
      // Remove addon (only from selection, NOT from cart)
      this.selectedAddons.delete(variantId);
      productCard.classList.remove('selected');
      const addText = button.querySelector('[data-add-text]');
      const removeText = button.querySelector('[data-remove-text]');
      if (addText) addText.style.display = 'inline';
      if (removeText) removeText.style.display = 'none';
    } else {
      // Add addon (only to selection, NOT to cart)
      const productTitle = productCard.querySelector('.addon-product-card__title').textContent;
      const variantTitle = variantSelect.selectedOptions?.[0]?.textContent || variantSelect.options?.[0]?.textContent || '';
      
      const addonData = {
        productId: productId,
        variantId: variantId,
        productTitle: productTitle,
        variantTitle: variantTitle,
        price: price
      };
      
      this.selectedAddons.set(variantId, addonData);
      productCard.classList.add('selected');
      const addText = button.querySelector('[data-add-text]');
      const removeText = button.querySelector('[data-remove-text]');
      if (addText) addText.style.display = 'none';
      if (removeText) removeText.style.display = 'inline';
    }
    
    this.updateTotal();
    this.updateSelectButton();
  }
  
  updateVariantPrice(select) {
    const productCard = select.closest('.addon-product-card');
    const button = productCard.querySelector('[data-addon-toggle-button]');
    const addText = button.querySelector('[data-add-text]');
    const price = parseInt(select.dataset.price);
    
    // Update button text with new price
    addText.textContent = `Add +${this.formatMoney(price).replace('.00', '')}`;
    
    // If this variant is selected, update the selected addon
    if (productCard.classList.contains('selected')) {
      const variantId = select.value;
      if (this.selectedAddons.has(variantId)) {
        this.selectedAddons.get(variantId).price = price;
        this.selectedAddons.get(variantId).variantTitle = select.selectedOptions?.[0]?.textContent || '';
        this.updateTotal();
      }
    }
  }
  
  updateTotal() {
    const totalElement = document.getElementById('addon-total');
    if (!totalElement) return;
    
    let total = 0;
    this.selectedAddons.forEach(addon => {
      total += addon.price;
    });
    
    totalElement.textContent = this.formatMoney(total);
  }
  
  updateSelectButton() {
    if (!this.selectButton) return;

    let total = 0;
    this.selectedAddons.forEach(addon => {
      total += addon.price;
    });

    if (total > 0) {
      this.selectButton.textContent = `Upgrade +${this.formatMoney(total).replace('.00', '')}`;
      this.selectButton.disabled = false;
    } else {
      this.selectButton.textContent = 'Select Upgrade';
      this.selectButton.disabled = true;
    }
  }
  
  updateExtrasIcon(count) {
    // Find all extras icons and update them
    document.querySelectorAll('.extras-icon').forEach(icon => {
      if (count > 0) {
        icon.textContent = count;
        icon.style.fontSize = '10px';
        icon.style.fontWeight = 'bold';
      } else {
        icon.textContent = '+';
        icon.style.fontSize = '';
        icon.style.fontWeight = '';
      }
    });
  }
  
  selectUpgrades() {
    // Save addons to storage (even if 0 - to clear previous selections)
    this.saveAddonsToStorage();
    
    // Update extras icon with count
    this.updateExtrasIcon(this.selectedAddons.size);
    
    // Close drawer
    this.closeDrawer();
    
    // Show success message
    if (this.selectedAddons.size > 0) {
      this.showSuccessMessage(`Updated: ${this.selectedAddons.size} add-on(s) selected. They will be added to cart when you click "Add to cart".`);
    } else {
      this.showSuccessMessage(`Add-ons cleared. No add-ons will be added to cart.`);
    }
  }
  
  saveAddonsToStorage() {
    const addonsArray = Array.from(this.selectedAddons.values());
    const mainProductId = this.getCurrentProductId();
    
    if (mainProductId) {
      if (addonsArray.length === 0) {
        // Clear storage if no add-ons selected
        localStorage.removeItem(`productAddons_${mainProductId}`);
      } else {
        const storageData = {
          addons: addonsArray,
          timestamp: Date.now()
        };
        
        localStorage.setItem(`productAddons_${mainProductId}`, JSON.stringify(storageData));
      }
    }
  }
  
  loadSavedAddons() {
    const mainProductId = this.getCurrentProductId();
    
    if (!mainProductId) {
      return;
    }
    
    try {
      const storageKey = `productAddons_${mainProductId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const data = JSON.parse(stored);
        
        if (data.addons && Array.isArray(data.addons)) {
          this.selectedAddons.clear();
          data.addons.forEach(addon => {
            this.selectedAddons.set(addon.variantId, addon);
          });
          this.updateUIFromStorage();
          
          // Update extras icon with saved count
          this.updateExtrasIcon(this.selectedAddons.size);
        }
      } else {
        // No saved addons, reset extras icon
        this.updateExtrasIcon(0);
      }
    } catch (error) {
      // Silent error handling
    }
  }
  
  updateUIFromStorage() {
    // Update product cards
    this.selectedAddons.forEach((addon, variantId) => {
      const productCard = document.querySelector(`[data-addon-product-id="${addon.productId}"]`);
      
      if (productCard) {
        const variantSelect = productCard.querySelector('[data-addon-variant-select]');
        
        if (variantSelect && variantSelect.value === variantId) {
          productCard.classList.add('selected');
          const button = productCard.querySelector('[data-addon-toggle-button]');
          if (button) {
            const addText = button.querySelector('[data-add-text]');
            const removeText = button.querySelector('[data-remove-text]');
            if (addText) addText.style.display = 'none';
            if (removeText) removeText.style.display = 'inline';
          }
        }
      }
    });
    
    this.updateTotal();
    this.updateSelectButton();
  }
  
  getCurrentProductId() {
    // Try to get product ID from various sources
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (productForm) {
      // Try to get from product data attribute
      const productData = productForm.dataset.productId;
      if (productData) {
        return productData;
      }
    }
    
    // Try to get from product JSON data
    const productDataScript = document.querySelector('script[type="application/json"][data-product-json]');
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent);
        if (productData.id) {
          return productData.id.toString();
        }
      } catch (error) {
        // Silent error handling
      }
    }
    
    // Try to get from meta tags
    const productIdMeta = document.querySelector('meta[property="product:id"]');
    if (productIdMeta) {
      const productId = productIdMeta.getAttribute('content');
      if (productId) {
        return productId;
      }
    }
    
    // Fallback: try to get from URL or other sources
    const pathParts = window.location.pathname.split('/');
    const productHandle = pathParts[pathParts.length - 1];
    return productHandle;
  }

  getSessionId() {
    // Get or create session ID (same as product-form.js)
    let sessionId = sessionStorage.getItem('cart_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('cart_session_id', sessionId);
    }
    return sessionId;
  }

  getProductMetafields() {
    const metafields = {
      center_stone_shape: '',
      carat_weight: '',
      material: ''
    };

    try {
      // Try to get from product JSON data
      const productDataScript = document.querySelector('script[type="application/json"][data-product-json]');
      if (productDataScript) {
        const productData = JSON.parse(productDataScript.textContent);
        if (productData.metafields) {
          metafields.center_stone_shape = productData.metafields['custom.center_stone_shape']?.value || '';
          metafields.carat_weight = productData.metafields['custom.carat_weight']?.value || '';
          metafields.material = productData.metafields['custom.material']?.value || '';
        }
      }

      // Fallback: Try to get from meta tags
      if (!metafields.center_stone_shape) {
        const centerStoneMeta = document.querySelector('meta[property="product:center_stone_shape"]');
        if (centerStoneMeta) {
          metafields.center_stone_shape = centerStoneMeta.getAttribute('content') || '';
        }
      }

      if (!metafields.carat_weight) {
        const caratWeightMeta = document.querySelector('meta[property="product:carat_weight"]');
        if (caratWeightMeta) {
          metafields.carat_weight = caratWeightMeta.getAttribute('content') || '';
        }
      }

      if (!metafields.material) {
        const materialMeta = document.querySelector('meta[property="product:material"]');
        if (materialMeta) {
          metafields.material = materialMeta.getAttribute('content') || '';
        }
      }

      // Fallback: Try to get from data attributes
      const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
      if (productForm) {
        if (!metafields.center_stone_shape) {
          metafields.center_stone_shape = productForm.dataset.centerStoneShape || '';
        }
        if (!metafields.carat_weight) {
          metafields.carat_weight = productForm.dataset.caratWeight || '';
        }
        if (!metafields.material) {
          metafields.material = productForm.dataset.material || '';
        }
      }
      
    } catch (error) {
      // Silent error handling
    }

    return metafields;
  }

  getFormProperties(form) {
    const properties = {};

    try {
      // Get all form elements with name starting with "properties["
      const propertyInputs = form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
      
      propertyInputs.forEach(input => {
        const name = input.name;
        let value = '';
        
        if (input.type === 'checkbox' || input.type === 'radio') {
          value = input.checked ? input.value : '';
        } else if (input.type === 'file') {
          // Handle file uploads if needed
          value = input.files.length > 0 ? input.files[0].name : '';
        } else {
          value = input.value || '';
        }
        
        // Only add if value is not empty
        if (value && value.trim() !== '') {
          // Extract property name from "properties[Property Name]"
          const match = name.match(/properties\[(.+)\]/);
          if (match) {
            const propertyName = match[1];
            properties[propertyName] = value.trim();
          }
        }
      });

      // Also check for any custom data attributes that might contain properties
      const customProperties = form.querySelectorAll('[data-property-name]');
      customProperties.forEach(element => {
        const propertyName = element.dataset.propertyName;
        const propertyValue = element.dataset.propertyValue || element.value || element.textContent;
        
        if (propertyName && propertyValue && propertyValue.trim() !== '') {
          properties[propertyName] = propertyValue.trim();
        }
      });
      
    } catch (error) {
      // Silent error handling
    }

    return properties;
  }

  getAddonMetafields(addon) {
    const metafields = {
      center_stone_shape: '',
      carat_weight: '',
      material: ''
    };

    try {
      // Try to get from addon data if available
      if (addon.metafields) {
        metafields.center_stone_shape = addon.metafields['custom.center_stone_shape']?.value || '';
        metafields.carat_weight = addon.metafields['custom.carat_weight']?.value || '';
        metafields.material = addon.metafields['custom.material']?.value || '';
      }

      // If addon doesn't have metafields, try to get from addon data attributes
      if (addon.element) {
        const addonElement = document.querySelector(`[data-addon-id="${addon.productId}"]`);
        if (addonElement) {
          metafields.center_stone_shape = addonElement.dataset.centerStoneShape || '';
          metafields.carat_weight = addonElement.dataset.caratWeight || '';
          metafields.material = addonElement.dataset.material || '';
        }
      }
      
    } catch (error) {
      // Silent error handling
    }

    return metafields;
  }
  
  interceptAddToCart() {
    let addToCartForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!addToCartForm) {
      return;
    }
    
    // Find product-form element
    const productFormElement = addToCartForm.closest('product-form');
    if (!productFormElement) {
      return;
    }
    
      // Check if we've already set up the interceptor for this form
      if (addToCartForm.hasAttribute('data-addon-intercepted')) {
        return;
      }
    
    // Clean up any existing listeners first
    this.cleanupEventListeners();
    
    // Mark this form as intercepted
    addToCartForm.setAttribute('data-addon-intercepted', 'true');
    
    // Note: No need to store original handler since product-form.js handles the logic
    
      // Create our custom handler
      const customHandler = async (e) => {
        // Always prevent default and stop propagation to avoid duplicate calls
        e.preventDefault();
        e.stopImmediatePropagation();

        // Global check to prevent duplicate processing
        if (window.addonDrawerProcessing) {
          return;
        }

        // Check if we have addons
        const mainProductId = this.getCurrentProductId();
        const savedAddons = this.getAddonsForProduct(mainProductId);

        // If no addons, let product-form.js handle it
        if (!savedAddons || savedAddons.length === 0) {
          return;
        }
      
        // If we have addons, handle it ourselves
        // Prevent multiple simultaneous add to cart operations
        if (this.isAddingToCart) {
          return;
        }
      
      // Set global processing flag
      window.addonDrawerProcessing = true;
      this.isAddingToCart = true;
      
      // Set loading state on submit button (similar to product-form.js)
      const submitButton = addToCartForm.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        
        // Show loading spinner if it exists
        const loadingSpinner = addToCartForm.querySelector('.loading__spinner');
        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }
      }
      
      
      try {
        // Get the main product variant ID
        const variantInput = addToCartForm.querySelector('input[name="id"]');
        if (!variantInput) return;
      
      const mainProductVariantId = variantInput.value;
      const mainProductId = this.getCurrentProductId();
      const quantity = addToCartForm.querySelector('input[name="quantity"]')?.value || 1;
      
      // Get addons for this product
      const savedAddons = this.getAddonsForProduct(mainProductId);
      
      // Get product metafields
      const productMetafields = this.getProductMetafields();
      
      // Get form properties (like ring size, etc.)
      const formProperties = this.getFormProperties(addToCartForm);
      
        // Create a single bundle ID for all items to ensure consistency
        // Include addon count and random string to make each bundle unique
        const addonCount = savedAddons ? savedAddons.length : 0;
        const randomString = Math.random().toString(36).substr(2, 9);
        const bundleId = `bundle_${mainProductId}_${addonCount}addons_${Date.now()}_${randomString}`;
        const cartTimestamp = Date.now().toString();
        const sessionId = this.getSessionId();
        
      // Create unique identifier to prevent quantity merging
      const uniqueId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      
      // Prepare cart items with properties
      const cartItems = [
        {
          id: mainProductVariantId,
          quantity: parseInt(quantity),
          properties: {
            '_is_main_product': 'true',
            '_addon_for': mainProductId,
            '_main_product_id': mainProductId,
            '_cart_timestamp': cartTimestamp,
            '_addon_count': savedAddons ? savedAddons.length : 0,
            '_bundle_id': bundleId,
            '_session_id': sessionId,
            '_unique_id': uniqueId,
            // Product metafields
            'custom.center_stone_shape': productMetafields.center_stone_shape || '',
            'custom.carat_weight': productMetafields.carat_weight || '',
            'custom.material': productMetafields.material || '',
            // Form properties
            ...formProperties
          }
        }
      ];
      
      // Add addons to cart items with properties
      if (savedAddons && savedAddons.length > 0) {
        savedAddons.forEach((addon, index) => {
          // Get addon metafields if available
          const addonMetafields = this.getAddonMetafields(addon);
          
          // Create unique ID for each addon
          const addonUniqueId = Date.now().toString() + Math.random().toString(36).substr(2, 9) + '_addon_' + (index + 1);
          
          cartItems.push({
            id: addon.variantId,
            quantity: 1,
            properties: {
              '_is_addon': 'true',
              '_addon_for': mainProductId,
              '_addon_product_id': addon.productId,
              '_addon_title': addon.productTitle,
              '_addon_variant_id': addon.variantId,
              '_addon_variant_title': addon.variantTitle || '',
              '_addon_price': addon.price || '',
              '_addon_type': addon.type || 'upgrade',
              '_cart_timestamp': cartTimestamp,
              '_bundle_id': bundleId,
              '_session_id': sessionId,
              '_unique_id': addonUniqueId,
              '_addon_index': index + 1,
              // Addon metafields
              'custom.center_stone_shape': addonMetafields.center_stone_shape || '',
              'custom.carat_weight': addonMetafields.carat_weight || '',
              'custom.material': addonMetafields.material || '',
              // Form properties (same as main product for addons)
              ...formProperties
            }
          });
        });
      }
      
        // Add all items to cart in a single request
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ 
            items: cartItems,
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname
          })
        });
        
        if (response.ok) {
          const responseData = await response.json();
          
          // Check for errors in response (similar to product-form.js)
          if (responseData.status) {
            // Handle error response
            this.showErrorMessage(responseData.description || 'Failed to add items to cart');
            return;
          }
          
          // Clear addons from storage after successful add to cart
          this.clearAddonsForProduct(mainProductId);
          
          // Clear all form data
          this.clearAllFormData(addToCartForm);
          
          // Update cart drawer with sections if available (similar to product-form.js)
          if (responseData.sections) {
            this.updateCartDrawerWithSections(responseData.sections);
          } else {
            // Fallback: trigger cart update event
            document.dispatchEvent(new CustomEvent('cart:updated'));

            // Additional fallback: try to refresh cart drawer manually
            this.refreshCartDrawerManually();
            
            // Ensure is-empty class is removed from cart drawer
            setTimeout(() => {
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer) {
                cartDrawer.classList.remove('is-empty');
              }
            }, 100);
          }
          
          // Publish cart update event (similar to product-form.js)
          if (window.PUB_SUB_EVENTS && window.publish) {
            const startMarker = window.CartPerformance ? window.CartPerformance.createStartingMarker('add:wait-for-subscribers') : null;
            
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'addon-drawer',
              productVariantId: mainProductVariantId,
              cartData: responseData,
            }).then(() => {
              if (startMarker && window.CartPerformance) {
                window.CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              }
            });
          }
          
          // Additional fallback: manually check cart state and update if needed
          setTimeout(() => {
            this.ensureCartDrawerIsUpdated();
          }, 200);
          
          // Show success message
          this.showAddToCartSuccess();
          
          // Reset ring size selector after successful add to cart
          setTimeout(() => {
            this.resetRingSizeSelector();
          }, 100);
          
          // Open cart drawer to show all items
          setTimeout(() => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              // Ensure is-empty class is removed before opening
              cartDrawer.classList.remove('is-empty');
              
              if (typeof cartDrawer.open === 'function') {
                cartDrawer.open();
              } else {
                // Fallback: try to trigger the cart drawer by clicking the cart icon
                const cartIcon = document.querySelector('#cart-icon-bubble');
                if (cartIcon) {
                  cartIcon.click();
                }
              }
            }
          }, 500);
          
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add items to cart');
        }
        
      } catch (error) {
        this.showErrorMessage('Error adding to cart: ' + error.message);
      } finally {
        // Reset loading state on submit button (similar to product-form.js)
        const submitButton = addToCartForm.querySelector('[type="submit"]');
        if (submitButton) {
          submitButton.classList.remove('loading');
          submitButton.removeAttribute('aria-disabled');
          
          // Hide loading spinner if it exists
          const loadingSpinner = addToCartForm.querySelector('.loading__spinner');
          if (loadingSpinner) {
            loadingSpinner.classList.add('hidden');
          }
        }
        
        this.isAddingToCart = false;
        window.addonDrawerProcessing = false;
      }
    };
    
    // Store the custom handler on the form for cleanup purposes
    addToCartForm._addonCustomHandler = customHandler;
    
    // Add our custom event listener
    addToCartForm.addEventListener('submit', customHandler);
    
    // Note: No need to override onSubmitHandler since product-form.js now checks for addons
  }
  
  // Method to clean up event listeners
  cleanupEventListeners() {
    const addToCartForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (addToCartForm) {
      // Remove our custom event listener if it exists
      if (addToCartForm._addonCustomHandler) {
        addToCartForm.removeEventListener('submit', addToCartForm._addonCustomHandler);
        delete addToCartForm._addonCustomHandler;
      }
      
      // Remove the intercepted flag
      addToCartForm.removeAttribute('data-addon-intercepted');
      
      // Note: No need to reset onSubmitHandler since we're not overriding it anymore
    }
  }
  
  // Method to clean up all resources
  cleanup() {
    this.cleanupEventListeners();
    if (this.formObserver) {
      this.formObserver.disconnect();
      this.formObserver = null;
    }
  }
  
  // Method to manually re-intercept the form (useful for debugging)
  reInterceptForm() {
    this.cleanupEventListeners();
    this.interceptAddToCart();
  }
  
  // Method to reset ring size selector specifically
  resetRingSizeSelector() {
    
    try {
      // Reset the select element
      const ringSizeSelect = document.getElementById('select-ring-size');
      if (ringSizeSelect) {
        ringSizeSelect.value = '';
        ringSizeSelect.selectedIndex = 0;
        
        // Update the selected value display
        const selectedValueSpan = ringSizeSelect.closest('.cstm-line-itemoption')?.querySelector('.selected-value');
        if (selectedValueSpan) {
          selectedValueSpan.textContent = 'Select Ring Size';
        }
        
        // Reset the ring size dropdown UI
        const ringSizeDropdown = ringSizeSelect.closest('.ring-size-dropdown');
        if (ringSizeDropdown) {
          const ringSizeButton = ringSizeDropdown.querySelector('.ring-size-button');
          const caretIcon = ringSizeDropdown.querySelector('.caret-icon');
          const options = ringSizeDropdown.querySelector('.ring-size-options');
          
          if (ringSizeButton) {
            ringSizeButton.setAttribute('aria-expanded', 'false');
            ringSizeButton.textContent = 'Select Ring Size';
          }
          
          if (caretIcon) {
            caretIcon.style.transform = 'rotate(0deg)';
          }
          
          if (options) {
            options.classList.add('hidden');
          }
        }
        
        // Trigger change event to update UI
        ringSizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        
          }
    } catch (error) {
      console.error('Error resetting ring size selector:', error);
    }
  }
  
  // Set up mutation observer to watch for form changes
  setupFormObserver() {
    if (this.formObserver) {
      this.formObserver.disconnect();
    }
    
    this.formObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if a new form was added
          const addedNodes = Array.from(mutation.addedNodes);
          const hasNewForm = addedNodes.some(node => 
            node.nodeType === Node.ELEMENT_NODE && 
            (node.matches && node.matches('form[data-type="add-to-cart-form"]') || 
             node.querySelector && node.querySelector('form[data-type="add-to-cart-form"]'))
          );
          
          if (hasNewForm) {
            // Small delay to ensure the form is fully rendered
            setTimeout(() => {
              this.interceptAddToCart();
            }, 100);
          }
        }
      });
    });
    
    // Start observing the document body for changes
    this.formObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
  
  getAddonsForProduct(productId) {
    try {
      const storageKey = `productAddons_${productId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const productAddons = JSON.parse(stored);
        const addons = productAddons.addons || [];
        return addons;
      }
    } catch (error) {
      // Silent error handling
    }
    return [];
  }
  
  clearAddonsForProduct(productId) {
    localStorage.removeItem(`productAddons_${productId}`);
  }
  
  clearAllFormData(form) {
    try {
      // Reset the form
      form.reset();
      
      // Clear ring size selector
      const ringSizeSelect = document.getElementById('select-ring-size');
      if (ringSizeSelect) {
        ringSizeSelect.value = '';
        ringSizeSelect.selectedIndex = 0;
        
        // Update the selected value display
        const selectedValueSpan = ringSizeSelect.closest('.cstm-line-itemoption')?.querySelector('.selected-value');
        if (selectedValueSpan) {
          selectedValueSpan.textContent = 'Select Ring Size';
        }
        
        // Reset the ring size dropdown UI
        const ringSizeDropdown = ringSizeSelect.closest('.ring-size-dropdown');
        if (ringSizeDropdown) {
          const ringSizeButton = ringSizeDropdown.querySelector('.ring-size-button');
          const caretIcon = ringSizeDropdown.querySelector('.caret-icon');
          const options = ringSizeDropdown.querySelector('.ring-size-options');
          
          if (ringSizeButton) {
            ringSizeButton.setAttribute('aria-expanded', 'false');
            ringSizeButton.textContent = 'Select Ring Size';
          }
          
          if (caretIcon) {
            caretIcon.style.transform = 'rotate(0deg)';
          }
          
          if (options) {
            options.classList.add('hidden');
          }
        }
        
        // Trigger change event to update UI
        ringSizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
      
      // Clear all radio button selections
      document.querySelectorAll('.cstm-line-itemoption input[type="radio"]').forEach(radio => {
        radio.checked = false;
        
        // Reset the parent option UI
        const optionContainer = radio.closest('.cstm-line-itemoption');
        if (optionContainer) {
          const selectedValueSpan = optionContainer.querySelector('.selected-value');
          if (selectedValueSpan) {
            selectedValueSpan.textContent = '';
          }
          
          // Remove active class if it exists
          optionContainer.classList.remove('active');
          
          // Reset dropdown UI if it's a dropdown
          const dropdown = optionContainer.querySelector('.ring-size-dropdown');
          if (dropdown) {
            const button = dropdown.querySelector('.ring-size-button');
            const caretIcon = dropdown.querySelector('.caret-icon');
            const options = dropdown.querySelector('.ring-size-options');
            
            if (button) {
              button.setAttribute('aria-expanded', 'false');
              button.textContent = 'Select Ring Size';
            }
            
            if (caretIcon) {
              caretIcon.style.transform = 'rotate(0deg)';
            }
            
            if (options) {
              options.classList.add('hidden');
            }
          }
        }
        
        // Trigger change event to update UI
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
      
      // Clear selected value displays for radio buttons
      document.querySelectorAll('.cstm-line-itemoption .selected-value').forEach(span => {
        if (span.textContent !== 'Select Ring Size') {
          span.textContent = '';
        }
      });
      
      // Note: Not removing 'active' class from .cli-item as requested
      
      // Clear addon selections after successful add to cart
      this.selectedAddons.clear();
      
      // Reset addon drawer UI to show no selections
      document.querySelectorAll('.addon-product-card').forEach(card => {
        card.classList.remove('selected');
        const button = card.querySelector('[data-addon-toggle-button]');
        if (button) {
          const addText = button.querySelector('[data-add-text]');
          const removeText = button.querySelector('[data-remove-text]');
          if (addText) addText.style.display = 'inline';
          if (removeText) removeText.style.display = 'none';
        }
      });
      
      // Update addon drawer totals and button to reflect current selections
      this.updateTotal();
      this.updateSelectButton();
      
      // Reset extras icon
      this.updateExtrasIcon(0);
      
      // Clear any custom form properties
      const propertyInputs = form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
      propertyInputs.forEach(input => {
        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = false;
        } else {
          input.value = '';
        }
      });
      
    } catch (error) {
      // Silent error handling
    }
  }
  
  updateCartDrawerWithSections(sections) {
    try {
      
          // Update cart drawer content
          if (sections['cart-drawer']) {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              
              // Parse the cart drawer HTML
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = sections['cart-drawer'];
              
              // Update the entire cart drawer structure, not just the items container
              // This ensures the empty state is properly handled
              const newCartDrawer = tempDiv.querySelector('cart-drawer');
              if (newCartDrawer) {
                
                // Update the main cart drawer content
                const cartDrawerInner = cartDrawer.querySelector('.drawer__inner');
                const newCartDrawerInner = newCartDrawer.querySelector('.drawer__inner');
                
                if (cartDrawerInner && newCartDrawerInner) {
                  cartDrawerInner.innerHTML = newCartDrawerInner.innerHTML;
                }
                
                // Also update the cart drawer items container specifically
                const cartItemsContainer = cartDrawer.querySelector('#CartDrawer-CartItems');
                const newCartItems = newCartDrawer.querySelector('#CartDrawer-CartItems');
                
                if (cartItemsContainer && newCartItems) {
                  cartItemsContainer.innerHTML = newCartItems.innerHTML;
                }
                
                // Remove is-empty class from cart drawer to show items
                cartDrawer.classList.remove('is-empty');
              } else {
                // Fallback: just update the items container
                const cartItemsContainer = cartDrawer.querySelector('#CartDrawer-CartItems');
                const newCartItems = tempDiv.querySelector('#CartDrawer-CartItems');
                
                if (cartItemsContainer && newCartItems) {
                  cartItemsContainer.innerHTML = newCartItems.innerHTML;
                }
                
                // Remove is-empty class from cart drawer to show items
                cartDrawer.classList.remove('is-empty');
              }
          
          // Re-initialize ring size dropdowns after update
          setTimeout(() => {
            // Call the global initializeRingSizeDropdowns function if it exists
            if (typeof window.initializeRingSizeDropdowns === 'function') {
              window.initializeRingSizeDropdowns();
            }
          }, 100);
        } else {
        }
      } else {
      }
      
      // Update cart icon bubble
      if (sections['cart-icon-bubble']) {
        const cartIconBubble = document.querySelector('#shopify-section-cart-icon-bubble');
        if (cartIconBubble) {
          cartIconBubble.innerHTML = sections['cart-icon-bubble'];
        } else {
          // Fallback: try to find cart-icon-bubble element
          const cartIconBubbleFallback = document.querySelector('cart-icon-bubble');
          if (cartIconBubbleFallback) {
            cartIconBubbleFallback.innerHTML = sections['cart-icon-bubble'];
          }
        }
      }
      
      // Trigger cart update event for other components
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Additional fallback: Check if cart drawer is properly updated
      setTimeout(() => {
        this.ensureCartDrawerIsUpdated();
      }, 500);
      
    } catch (error) {
      // Fallback to regular cart update
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
      // Additional fallback: Try to reload cart completely
      setTimeout(() => {
        if (window.reloadCart) {
          window.reloadCart();
        }
      }, 1000);
    }
  }
  
  async refreshCartDrawerManually() {
    try {
      // Fetch fresh cart drawer content
      const response = await fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble');
      if (!response.ok) {
        throw new Error('Failed to fetch cart sections');
      }

      const html = await response.text();
      const sectionsData = JSON.parse(html);

      // Update cart drawer
      if (sectionsData['cart-drawer']) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sectionsData['cart-drawer'];
          const newCartDrawer = tempDiv.querySelector('cart-drawer');

          if (newCartDrawer) {
            const cartDrawerInner = cartDrawer.querySelector('.drawer__inner');
            const newCartDrawerInner = newCartDrawer.querySelector('.drawer__inner');

            if (cartDrawerInner && newCartDrawerInner) {
              cartDrawerInner.innerHTML = newCartDrawerInner.innerHTML;
            }
            
            // Remove is-empty class from cart drawer to show items
            cartDrawer.classList.remove('is-empty');
          }
        }
      }

      // Update cart icon bubble
      if (sectionsData['cart-icon-bubble']) {
        const cartIconBubble = document.querySelector('#shopify-section-cart-icon-bubble');
        if (cartIconBubble) {
          cartIconBubble.innerHTML = sectionsData['cart-icon-bubble'];
        }
      }

      // Re-initialize ring size dropdowns
      if (typeof window.initializeRingSizeDropdowns === 'function') {
        window.initializeRingSizeDropdowns();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  checkCartEmptyState() {
    try {
      // Check if cart is empty by looking for empty state elements
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;
      
      const emptyState = cartDrawer.querySelector('.drawer__inner-empty');
      const cartItems = cartDrawer.querySelectorAll('.cart-item-group');
      
      // If we have items but still showing empty state, remove is-empty class
      if (cartItems.length > 0 && cartDrawer.classList.contains('is-empty')) {
        cartDrawer.classList.remove('is-empty');
      }
    } catch (error) {
      // Silent error handling
    }
  }
  
  ensureCartDrawerIsUpdated() {
    try {
      // Fetch current cart state
      fetch('/cart.js')
        .then(response => response.json())
        .then(cartData => {
          const cartDrawer = document.querySelector('cart-drawer');
          if (!cartDrawer) {
            return;
          }
          
          // Check if cart is empty
          if (cartData.items.length === 0) {
            // Check if empty state is already shown
            const emptyState = cartDrawer.querySelector('.drawer__inner-empty');
            if (!emptyState) {
              // Trigger a cart reload to ensure proper empty state
              if (window.reloadCart) {
                window.reloadCart();
              }
            }
          } else {
            // Cart has items, ensure is-empty class is removed
            cartDrawer.classList.remove('is-empty');
            
            // Check if items are displayed
            const cartItems = cartDrawer.querySelectorAll('.cart-item-group');
            if (cartItems.length === 0) {
              // Trigger a cart reload to ensure proper item display
              if (window.reloadCart) {
                window.reloadCart();
              }
            }
          }
        })
        .catch(error => {
          // Silent error handling
        });
    } catch (error) {
      // Silent error handling
    }
  }
  
  updateCartCount() {
    // Trigger cart count update
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCount = cart.item_count;
        const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cartCount;
        });
      })
      .catch(error => {
        // Silent error handling
      });
  }
  
  
  showAddToCartSuccess() {
    this.showSuccessMessage('Successfully added to cart!');
  }
  
  showSuccessMessage(message) {
    // Create success notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #28a745;
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
    `;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Auto remove after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  }
  
  showErrorMessage(message) {
    // Create error notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
    `;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Auto remove after 4 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 4000);
  }
  
  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  }
}

// Function to debug localStorage
function debugLocalStorage() {
  const mainProductId = window.addonDrawerInstance?.getCurrentProductId();
  
  if (mainProductId) {
    const storageKey = `productAddons_${mainProductId}`;
    const stored = localStorage.getItem(storageKey);
    
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return parsed.addons || [];
      } catch (e) {
        return [];
      }
    }
  }
  
  return addonKeys;
}

// Initialize addon drawer
document.addEventListener('DOMContentLoaded', () => {
  // Prevent multiple initializations
  if (window.addonDrawerInstance) {
    return;
  }
  
  // Only initialize on product pages
  if (document.body.classList.contains('product')) {
    window.addonDrawerInstance = new AddonDrawer();
  }
});
